import { z } from "zod"
import { Id } from "./ids.js"

/** Taxonomy v1; the list lives in @vienna/ai and is mirrored here for the API surface. */
export const AssessmentCategory = z.enum(["spam", "scam", "targeted_hate", "harassment", "threat", "graphic_violence", "sexual", "profanity", "self_promotion", "off_topic", "complaint", "question", "purchase_intent", "praise"])
export type AssessmentCategory = z.infer<typeof AssessmentCategory>
export const RiskLevel = z.enum(["high", "review"])
export type RiskLevel = z.infer<typeof RiskLevel>
export const SuggestionKind = z.enum(["hide", "review", "route", "signal", "allow"])
export type SuggestionKind = z.infer<typeof SuggestionKind>

/** The classifier's reading of one comment. Scores are risk signals, not calibrated probabilities. */
export const CommentAssessment = z.object({
  id: Id,
  status: z.enum(["pending", "complete", "failed", "skipped"]),
  categories: z.record(z.string(), z.number()),
  sentiment: z.number().nullable(),
  language: z.string().nullable(),
  needsHuman: z.boolean(),
  summary: z.string().nullable(),
  risk: z.object({ category: AssessmentCategory, level: RiskLevel }).nullable(),
  flags: z.array(z.string()),
  model: z.string(),
  promptVersion: z.string(),
  taxonomyVersion: z.string(),
  policyRevision: z.number().int(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
})
export type CommentAssessment = z.infer<typeof CommentAssessment>

/** What policy made of the assessment. In suggest mode nothing was executed. */
export const ModerationSuggestion = z.object({
  id: Id,
  decision: SuggestionKind,
  category: AssessmentCategory,
  score: z.number(),
  riskLevel: RiskLevel,
  mode: z.enum(["suggest", "act", "hold"]),
  ruleName: z.string().nullable(),
  createdAt: z.iso.datetime(),
})
export type ModerationSuggestion = z.infer<typeof ModerationSuggestion>

export const ModerationPolicy = z.object({
  text: z.string(),
  revision: z.number().int(),
  paused: z.boolean(),
  monthlyTokenBudget: z.number().int(),
  tokensUsedThisMonth: z.number().int(),
  updatedByName: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** How assessments run on this deployment: a real model, the deterministic sandbox, or not at all. */
  provider: z.enum(["anthropic", "sandbox", "off"]),
  model: z.string(),
})
export type ModerationPolicy = z.infer<typeof ModerationPolicy>
export const SaveModerationPolicyInput = z.object({ text: z.string().max(4000), paused: z.boolean(), monthlyTokenBudget: z.number().int().min(0).max(1_000_000_000) })
export type SaveModerationPolicyInput = z.infer<typeof SaveModerationPolicyInput>

export const SuggestionActivity = z.object({ id: Id, commentId: Id, commentText: z.string().nullable(), decision: SuggestionKind, category: AssessmentCategory, score: z.number(), riskLevel: RiskLevel, mode: z.enum(["suggest", "act", "hold"]), summary: z.string().nullable(), createdAt: z.iso.datetime() })
export type SuggestionActivity = z.infer<typeof SuggestionActivity>

// ---- Notifications ----

export const Notification = z.object({ id: Id, kind: z.string(), title: z.string(), body: z.string().nullable(), link: z.string().nullable(), readAt: z.iso.datetime().nullable(), createdAt: z.iso.datetime() })
export type Notification = z.infer<typeof Notification>
export const NotificationsPage = z.object({ notifications: z.array(Notification), unread: z.number().int() })
export type NotificationsPage = z.infer<typeof NotificationsPage>
export const MarkNotificationsReadInput = z.object({ ids: z.array(Id).max(200).optional() })
